/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/accountUsers.scss';

import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import AccountUser from "../../../models/accountUserModel";
import {AppContext} from '../../misc/appContext';
import {createErrorMessage} from "../../../util/utils";
import {ActivateHandle} from "../../../util/interfaces";
import {onSetCrumbs} from "../../../util/events";
import Invitation from "../../../models/invitationModel";
import {AccountInvitations, AccountUsers} from "./accountUsers";
import {onShowConfirmSnackbar} from "../../../util/renderUtil";
import UserRightsController from "./userRights/userRightsController";
import {useIsFirstRender} from "../../../util/componentUtil";
import InfoBanner from "../../misc/infoBanner";

type LoadParams = { search?: string; page?: number; showLoad?: boolean; };

const USERS_LIMIT = 15;
const INVITATIONS_LIMIT = 10;

export default function AccountUsersController() {
    const context = useContext(AppContext);
    const firstRender = useIsFirstRender()
    const {account} = context.accountContext;
    const [accountUsers, setAccountUsers] = useState<Array<AccountUser>>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [invitations, setInvitations] = useState<Array<Invitation>>([]);
    const [totalInvitations, setTotalInvitations] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingInvitations, setLoadingInvitations] = useState(true);
    const [usersPage, setUsersPage] = useState(0);
    const [invitationsPage, setInvitationsPage] = useState(0);
    const usersAbortController = useRef<AbortController | null>(null);
    const invitationsAbortController = useRef<AbortController | null>(null);
    const accessRef = useRef<ActivateHandle>(null);

    function onChangePage(type: 'users' | 'invitations') {
        return function (page: number) {
            return function () {
                if (type === 'users') {
                    setUsersPage(page);
                    loadAccountUsers({page});
                } else {
                    setInvitationsPage(page);
                    loadInvitations({page});
                }
            }
        }
    }

    function onClickInvite() {
        accessRef.current?.activate();
    }

    function onClickUser(accountUser: AccountUser) {
        return function () {
            accessRef.current?.activate(accountUser);
        }
    }

    function onClickRevoke(accountUser: AccountUser) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            showDialog('Weet je zeker dat je de toegang van deze gebruiker tot jouw account wilt verwijderen?',
                'Toegang verwijderen', ['Nee', {label: 'Ja', onClick: onConfirmRevoke(accountUser), primary: false}]);
        }
    }

    function onConfirmRevoke(accountUser: AccountUser) {
        return function () {
            setLoadingUsers(true);
            accountUser.revoke((_: AccountUser, success: boolean, data: ResponseData) => {
                if (success) {
                    loadAccountUsers();
                    showSnackbar('De toegang tot het account is ingetrokken')
                }
                else {
                    setLoadingUsers(false);
                    showDialog(createErrorMessage(data.message ?? 'Het verwijderen van de gebruiker is niet gelukt.'),
                        'Verwijderen mislukt');
                }
            })
        }
    }

    function onSendInvitation(invitation: Invitation) {
        return function () {
            setLoadingInvitations(true);
            invitation.send((invitation: Invitation, success: boolean, data: ResponseData) => {
                setLoadingInvitations(false);
                if (success) {
                    onShowConfirmSnackbar('De uitnodiging is opnieuw verzonden');
                    const _invitations = invitations.slice();
                    for (let i = 0; i < _invitations.length; i++)
                        if (_invitations[i].id === invitation.id) {
                            _invitations.splice(i, 1, invitation);
                            setInvitations(_invitations);
                            break;
                        }
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het verzenden van de uitnodiging is niet gelukt'),
                        'Uitnodiging verzenden mislukt');
            })
        }
    }

    function onDeleteInvitation(invitation: Invitation) {
        return function () {
            showDialog('Weet je zeker dat je deze uitnodiging wilt intrekken?', 'Uitnodiging intrekken',
                ['Nee', {label: 'Ja', onClick: onConfirmDeleteInvitation(invitation), primary: false}]);
        }
    }

    function onConfirmDeleteInvitation(invitation: Invitation) {
        return function () {
            setLoadingInvitations(true);
            invitation.delete((_invitation: Invitation, success: boolean, data: ResponseData) => {
                if (success) {
                    loadInvitations();
                    showSnackbar('De uitnodiging is ingetrokken');
                } else {
                    setLoadingInvitations(false);
                    showDialog(createErrorMessage(data.message ?? 'Het intrekken van de uitnodiging is niet gelukt.'),
                        'Intrekken mislukt');
                }
            });
        }
    }

    function loadAll(showLoad: boolean = true) {
        return function() {
            loadAccountUsers({showLoad});
            loadInvitations({showLoad});
        }
    }

    function loadAccountUsers(params?: LoadParams) {
        abortRequest('users');
        if (!account?.id) return;
        if (params?.showLoad !== false)
            setLoadingUsers(true);
        const _page = params?.page ?? usersPage;
        usersAbortController.current = AccountUser.getAll(account.id, null, _page, USERS_LIMIT,
            (accountUsers: Array<AccountUser>, success: boolean, data: ResponseData) => {
                usersAbortController.current = null;
                setLoadingUsers(false);
                if (success) {
                    setAccountUsers(accountUsers);
                    setTotalUsers(data.total);
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het laden van de gebruikers is niet gelukt.'),
                        'Laden mislukt');
            });
    }

    function loadInvitations(params?: LoadParams) {
        abortRequest('invitations');
        if (!account) return;
        if (params?.showLoad !== false)
        setLoadingInvitations(true);
        const _page = params?.page ?? usersPage;
        invitationsAbortController.current = Invitation.getAll(account.id, null, _page, USERS_LIMIT,
            (invitations: Array<Invitation>, success: boolean, data: ResponseData) => {
                invitationsAbortController.current = null;
                setLoadingInvitations(false);
                if (success) {
                    setInvitations(invitations);
                    setTotalInvitations(data.total);
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het laden van de uitnodigingen is niet gelukt.'),
                        'Laden mislukt');
            })
    }

    function abortRequest(type: 'users' | 'invitations') {
        if (type === 'users') {
            const controller = usersAbortController.current;
            if (controller) {
                controller.abort();
                usersAbortController.current = null;
            }
        } else {
            const controller = invitationsAbortController.current;
            if (controller) {
                controller.abort();
                invitationsAbortController.current = null;
            }
        }
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Gebruikersbeheer'}]);

        return function () {
            abortRequest('users');
            abortRequest('invitations');
        }
    }, []);

    useEffect(() => {
        loadAll(firstRender)();

        return function () {
            abortRequest('users');
            abortRequest('invitations');
        }
    }, [context.accountContext.account]);

    return (
        <>
            <div className='account-users-page'>
                <InfoBanner featureKey='account_users'
                              variant='info'
                              title='Gebruikers'
                              message='Voeg hier nieuwe gebruikers toe aan het Bolmate account.'
                              showForNewUsersOnly={true}
                />
                <AccountUsers onClickUser={onClickUser} onClickRevoke={onClickRevoke} accountUsers={accountUsers}
                              onChangePage={onChangePage('users')} loading={loadingUsers} page={usersPage}
                              limit={USERS_LIMIT}
                              total={totalUsers}/>
                <AccountInvitations onClickInvite={onClickInvite} onDeleteInvitation={onDeleteInvitation}
                                    onSendInvitation={onSendInvitation} invitations={invitations}
                                    onChangePage={onChangePage('invitations')} loading={loadingInvitations}
                                    page={invitationsPage} limit={INVITATIONS_LIMIT} total={totalInvitations}/>
                <UserRightsController ref={accessRef} onSuccess={loadAll(false)}/>
            </div>
        </>
    )
}